/* External */
import {
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { fromJS } from 'immutable';

/* Utils */
import { useIsForegroundApp } from '../ForegroundAppProvider';
import {
  getUserAccountFromIdToken,
  addAccessTokenDataSubscriber,
  removeAccessTokenDataSubscriber,
  getAccessTokenData,
} from '../../utils/accessTokenData';
import { mergeDeepWithLists } from '../../utils/functions';
import { fetchUserCustomData, updateUserCustomData as updateCustomData } from './utils';
import {
  UpdateUserCustomDataContext,
  UserAccountContext,
  UserAccountLoadingContext,
  UserCustomDataContext,
  UserCustomDataUpdatingContext,
} from './contexts';

function UserProvider({ children = undefined }) {
  const [idToken, setIdToken] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [userCustomData, setUserCustomData] = useState(undefined);
  const [isUpdatingCustomData, setIsUpdatingCustomData] = useState(true);

  const isForegroundApp = useIsForegroundApp();

  const parseAndSetUserCustomData = useCallback((newUserCustomData) => {
    if (newUserCustomData) {
      setUserCustomData((currentUserCustomData) => {
        const parsedUserCustomData = fromJS(newUserCustomData);
        if (!currentUserCustomData) return parsedUserCustomData;

        return mergeDeepWithLists(currentUserCustomData, parsedUserCustomData);
      });
    } else {
      setUserCustomData(undefined);
    }
  }, []);

  const fetchAndSetUserCustomData = useCallback(async () => {
    setIsUpdatingCustomData(true);
    try {
      const newUserCustomData = await fetchUserCustomData();
      parseAndSetUserCustomData(newUserCustomData);
    } catch (error) {
      console.error('Error fetching user custom data', error);
    }
    setIsUpdatingCustomData(false);
  }, [parseAndSetUserCustomData]);

  const updateUserCustomData = useCallback(async (customData) => {
    setIsUpdatingCustomData(true);
    try {
      const newUserCustomData = await updateCustomData(customData);
      parseAndSetUserCustomData(newUserCustomData);
    } catch (error) {
      console.error('Error updating user custom data', error);
    }
    setIsUpdatingCustomData(false);
  }, [parseAndSetUserCustomData]);

  const userAccount = useMemo(() => getUserAccountFromIdToken(idToken), [idToken]);

  useEffect(() => {
    if (isForegroundApp) {
      fetchAndSetUserCustomData();
    }
  }, [fetchAndSetUserCustomData, isForegroundApp]);

  useEffect(() => {
    async function updateIdToken() {
      const { idToken: newIdToken = undefined } = (await getAccessTokenData()) || {};
      fetchAndSetUserCustomData();
      setIdToken(newIdToken);
      setIsLoading(false);
    }
    updateIdToken();
    addAccessTokenDataSubscriber(updateIdToken);
    return () => {
      removeAccessTokenDataSubscriber(updateIdToken);
    };
  }, [fetchAndSetUserCustomData]);

  return (
    <UpdateUserCustomDataContext.Provider value={updateUserCustomData}>
      <UserAccountLoadingContext.Provider value={isLoading}>
        <UserCustomDataUpdatingContext.Provider value={isUpdatingCustomData}>
          <UserAccountContext.Provider value={userAccount}>
            <UserCustomDataContext.Provider value={userCustomData}>
              {children}
            </UserCustomDataContext.Provider>
          </UserAccountContext.Provider>
        </UserCustomDataUpdatingContext.Provider>
      </UserAccountLoadingContext.Provider>
    </UpdateUserCustomDataContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.node,
};

export default memo(UserProvider);
